import React, { useEffect, useState } from 'react';
import { InputEmail } from '@jobber/components/InputEmail';
import { InputPassword } from '@jobber/components/InputPassword';
import { showToast } from '@jobber/components/Toast';
import { Button } from '@jobber/components/Button';
import { apiPost, apiGet } from '../../api-client';
import { useNavigate } from 'react-router';
import { Heading } from '@jobber/components/Heading';
import { Link, useLocation } from 'react-router-dom';
import { Checkbox } from '@jobber/components/Checkbox';
import { isValidEmail, validatePassword, validatePasswordChar } from '../../components/Common/Validations';
import { Spinner } from '@jobber/components/Spinner';
import { useDispatch } from 'react-redux';
import { setLoginUser } from '../../store/userSlice';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/use-auth';
import logo from '../../assets/logoBlack.svg';
import Footer from './Components/Footer';
import { hasJobberAccess } from '../../utils/auth';

function Signin() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const invitationToken = searchParams.get('invitationToken');
  const shouldConnectJobber = searchParams.get('connectJobber') === 'true';
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [loginButton, setLoginButton] = useState(false);
  const [loading, setLoading] = useState(false);
  const [jobberLoading, setJobberLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [formValues, setFormValues] = useState({
    email: '',
    password: ''
  });
  const setValuesHandler = (name, value) => {
    setFormValues({ ...formValues, [name]: value });
  };

  const submitHandler = async (e) => {
    e.preventDefault()
    setLoading(true);
    if (!formValues.email || !formValues.password) {
      showToast({ message: 'Please fill out all fields', variation: 'error' });
    } else if (!isValidEmail(formValues.email)) {
      showToast({ message: 'Email is not valid', variation: 'error' });
    } else if (!validatePassword(formValues.password)) {
      showToast({
        message: 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
        variation: 'error'
      });
    } else {
      try {
        const result = await apiPost(`user/login`, { ...formValues, rememberMe, invitationToken });
        if (result.status) {
          const user = result.data?.data;

          // Update AuthProvider's session query cache so isAuthenticated becomes true immediately
          queryClient.setQueryData(['session'], { status: true, data: { data: user } });

          setUserData(user);

          const [tableSettings, quoteSettings, jobSettings, visitSettings, approvedQuoteSettings] = await Promise.all([
            apiGet(`table-settings/${user._id}`),
            apiGet(`page-settings/${user._id}`),
            apiGet(`job-table-settings/${user._id}`),
            apiGet(`visit-table-Settings/${user._id}`),
            apiGet(`approved-quote/${user._id}`)
          ]);

          if (tableSettings.status && quoteSettings.status && jobSettings.status && visitSettings.status) {
            localStorage.setItem('userSettings', JSON.stringify(tableSettings.data));
            localStorage.setItem('userQuotesSettings', JSON.stringify(quoteSettings.data));
            localStorage.setItem('approvedQuotesSettings', JSON.stringify(approvedQuoteSettings.data));
            localStorage.setItem('jobSettings', JSON.stringify(jobSettings.data));
            localStorage.setItem('visitSettings', JSON.stringify(visitSettings.data));
            dispatch(setLoginUser({ data: user }));
            localStorage.removeItem('cTab');
            showToast({
              message: 'User successfully login',
              variation: 'success'
            });
            if (hasJobberAccess(user)) {
              document.body.classList.add('body-pd');
              navigate('/clients');
            } else {
              setLoginButton(true);
            }
          } else {
            showToast({
              message: tableSettings.message || tableSettings.error,
              variation: 'error'
            });
          }
        } else {
          showToast({
            message: result.message || result.error,
            variation: 'error'
          });
        }
      } catch (error) {
        showToast({
          message: error.message || 'Login failed',
          variation: 'error'
        });
      }
    }
    setLoading(false);
  };

  const { user: authUser, isAuthenticated } = useAuth();

  // If already authenticated via AuthProvider, redirect immediately (no extra API call)
  useEffect(() => {
    if (isAuthenticated && hasJobberAccess(authUser)) {
      navigate('/clients');
      return;
    }

    if (isAuthenticated && authUser && !hasJobberAccess(authUser)) {
      setUserData(authUser);
      setLoginButton(true);
    }
  }, [isAuthenticated, authUser?._id, authUser?.isConnected, authUser?.jobberConnection?.accountId, shouldConnectJobber]);

  const loginWithJobberHandler = () => {
    setJobberLoading(true);
    const userId = userData?._id;
    if (userId) {
      window.location.href = process.env.REACT_APP_API_URL + '/auth';
    }
  };

  return (
    <div className="row">
      <div className="text-center mb-4">
        <Heading level={1}>JOBBER X THE ROYAL GATEWAY</Heading>
      </div>
      <div className="col-md-4"></div>
      <div className="col-md-4">
        <div className="d-flex justify-content-between mb-3">
          <Heading level={3}>Login</Heading>
          <div className="d-flex gap-3">
            <Link to={'/forgot-password'}>Forgot Password?</Link>
            <Link to={'/signup'}>Signup</Link>
          </div>
        </div>
        <div className="card">
          {!loginButton ? (
            <form className="card-body" onSubmit={submitHandler}>
              <InputEmail
                placeholder="Email"
                name="email"
                value={formValues.email}
                onChange={(value) => setValuesHandler('email', value)}
                validations={{
                  required: {
                    value: true,
                    message: 'Tell us your email'
                  }
                }}
              />
              <div className="mt-3"></div>

              <InputPassword
                placeholder="Password"
                name="password"
                hasVisibility={true}
                value={formValues.password}
                onChange={(value) => setValuesHandler('password', value)}
                validations={{
                  required: { value: true, message: 'Password is required' },
                  validate: validatePasswordChar,
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters'
                  }
                }}
              />
              <div className="mt-3">
                <Checkbox
                  label="Remember me"
                  checked={rememberMe}
                  onChange={(value) => setRememberMe(value)}
                />
              </div>
              <div className="mt-3"></div>
              {loading ? <Spinner size="base" /> : <Button label={'Login'} submit />}
            </form>
          ) : (
            <>
              <div className="text-center mt-3">
                <p className="fs-6">Please login with jobber to continue</p>
              </div>
              <div
                onClick={loginWithJobberHandler}
                className="mt-2 mb-3 pointer border rounded ms-5 me-5 pt-3 pb-3 d-flex gap-3 align-items-center justify-content-center"
              >
                {jobberLoading ? <Spinner size="base" /> : <img src={logo} alt="" />}
                <Heading level={2}>Connnect Jobber</Heading>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="col-md-4"></div>
      <Footer />
    </div>
  );
}

export default Signin;
